export type RuntimeEnvKind = "local" | "preview" | "production";

/**
 * What the gates read of one capability feature: its mode in each kind of
 * runtime. @libs/environment-contracts' feature definitions carry more (the
 * resources, operations and env a deploy provisions); the gates need only this.
 */
export interface GatedFeature {
  environments: Readonly<Record<RuntimeEnvKind, { mode: string }>>;
}

/** A capability's features, by feature name. */
export type FeatureDefinitions = Readonly<Record<string, GatedFeature>>;

/** The features of one capability a service selects. */
export interface CapabilitySelection {
  capability: string;
  features: readonly string[];
}

// LISSEN_ENV values follow a convention: "local-<user>", "pr-<n>", or anything
// else (e.g. "prod", "test") which is treated as a stable runtime.
export function classifyRuntimeEnv(lissenEnv: string): RuntimeEnvKind {
  if (lissenEnv.startsWith("local-")) return "local";
  if (lissenEnv.startsWith("pr-")) return "preview";
  return "production";
}

/** `<capability>` or `<capability>.<feature>` of these definitions. */
export type GateNameOf<Definitions extends Record<string, FeatureDefinitions>> =
  | (keyof Definitions & string)
  | {
      [Capability in keyof Definitions & string]: `${Capability}.${keyof Definitions[Capability] & string}`;
    }[keyof Definitions & string];

export interface RuntimeGates<Name extends string = string> {
  /**
   * True when the capability has at least one selected feature whose policy
   * is `managed` in the current runtime env. Accepts a bare capability name
   * ("branch") or a fully-qualified feature name ("branch.linking").
   */
  allowed(name: Name): boolean;
  require(name: Name): void;
  env(name: string): string;
  optionalEnv(name: string): string | undefined;
  runtime: {
    lissenEnv: string;
    envKind: RuntimeEnvKind;
    isPreview: boolean;
  };
}

const missingLissenEnv =
  "Missing required runtime env var: LISSEN_ENV. Every service must declare its environment (prod, pr-<n>, local-<user>).";

/**
 * The runtime gates of a service: which of its selected capability features
 * are managed in the env's LISSEN_ENV. It reads only the definitions it is
 * given, so a client (the mobile app) can pass in its own features without
 * carrying every service's capabilities.
 */
export function createRuntimeGates<Name extends string = string>(args: {
  definitions: Record<string, FeatureDefinitions>;
  selections: readonly CapabilitySelection[];
  env: Record<string, string | undefined>;
}): RuntimeGates<Name> {
  // Eager check so misconfigured services fail at module load. The lookups
  // below re-read `args.env.LISSEN_ENV` lazily so mutations after
  // registration (e.g. test stubs) are still observed.
  if (!args.env.LISSEN_ENV) throw new Error(missingLissenEnv);

  function resolveEnvKind(): RuntimeEnvKind {
    const lissenEnv = args.env.LISSEN_ENV;
    if (!lissenEnv) throw new Error(missingLissenEnv);
    return classifyRuntimeEnv(lissenEnv);
  }

  function managedSetsFor(envKind: RuntimeEnvKind) {
    const capabilities = new Set<string>();
    const features = new Set<string>();
    for (const selection of args.selections) {
      const capability = args.definitions[selection.capability];
      if (!capability) continue;
      for (const feature of selection.features) {
        const policy = capability[feature]?.environments[envKind];
        if (policy?.mode === "managed") {
          capabilities.add(selection.capability);
          features.add(`${selection.capability}.${feature}`);
        }
      }
    }
    return { capabilities, features };
  }

  const allowed = (name: Name): boolean => {
    const sets = managedSetsFor(resolveEnvKind());
    return sets.features.has(name) || sets.capabilities.has(name);
  };

  return {
    allowed,
    require(name) {
      if (!allowed(name)) {
        throw new Error(`Capability is not enabled in this runtime: ${name}`);
      }
    },
    env(name) {
      const value = args.env[name];
      if (!value) throw new Error(`Missing required runtime env var: ${name}`);
      return value;
    },
    optionalEnv(name) {
      return args.env[name];
    },
    get runtime() {
      const envKind = resolveEnvKind();
      return {
        lissenEnv: args.env.LISSEN_ENV as string,
        envKind,
        isPreview: envKind !== "production",
      };
    },
  };
}
